import { z } from 'zod';
import { CONVERTED_LEAD_STATUS } from '../enums.js';
import {
  listFiltersQueryFragment,
  optionalDate,
  optionalEmail,
  optionalNumber,
  optionalPhone,
  optionalText,
  optionalUrl,
  paginationQuerySchema,
  toPatchSchema,
  uuidSchema,
} from './common.js';
import { personNameSchema } from './auth.js';
import { businessManagerFilterSchema, type BusinessManagerRef, type RecordRef } from './records.js';

/** One picklist value: a short, non-empty string the service then checks. */
const picklistValue = z.string().trim().min(1).max(80);

export const createLeadSchema = z.object({
  firstName: optionalText(80),
  lastName: personNameSchema.max(80),
  businessName: optionalText(200),
  title: optionalText(120),
  email: optionalEmail(),
  phone: optionalPhone(),
  mobile: optionalPhone(),
  website: optionalUrl(),
  /*
   * Shape only. Which values are *allowed* is the organisation's own system
   * picklist, which only the database knows — the service checks it, at the
   * one point every write path goes through.
   *
   * `CONVERTED` is refused here rather than there, because it is refused for
   * everybody: a lead becomes converted by going through the conversion
   * endpoint, which creates the account, contact and deal in one transaction.
   * Setting the status directly would produce a lead marked converted with
   * nothing to show for it.
   */
  status: picklistValue.refine(
    (value) => value !== CONVERTED_LEAD_STATUS,
    'Convert the lead instead of setting this',
  ).default('NEW'),
  source: picklistValue.default('OTHER'),
  /** The markets this lead sells into. Carried across when it converts. */
  verticals: z.array(picklistValue).max(20).optional(),
  businessCategory: optionalText(120),
  description: optionalText(4000),

  street: optionalText(200),
  city: optionalText(120),
  state: optionalText(120),
  postalCode: optionalText(32),
  country: optionalText(120),

  /** Source attribution: which campaign this lead came from. */
  campaignId: uuidSchema.nullish(),
  businessManagerId: uuidSchema.optional(),
  customFields: z.record(z.string(), z.unknown()).optional(),
});
export type CreateLeadInput = z.infer<typeof createLeadSchema>;

/**
 * CONVERTED is excluded from writes on purpose: a lead becomes converted by
 * going through the conversion endpoint, which creates the account, contact and
 * deal in one transaction. Letting a client set the status directly would
 * produce a lead marked converted with nothing to show for it.
 */
export const updateLeadSchema = toPatchSchema(createLeadSchema);
export type UpdateLeadInput = z.infer<typeof updateLeadSchema>;

export const LEAD_SORT_FIELDS = [
  'createdAt',
  'updatedAt',
  'lastName',
  'businessName',
  'status',
] as const;

export const listLeadsQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(LEAD_SORT_FIELDS).default('createdAt'),
  status: picklistValue.optional(),
  source: picklistValue.optional(),
  /** Leads in any of these markets. */
  vertical: picklistValue.optional(),
  businessManagerId: businessManagerFilterSchema.optional(),
  /** Hides leads already converted, which is the usual working view. */
  openOnly: z.stringbool().optional(),
  ...listFiltersQueryFragment,
});
export type ListLeadsQuery = z.infer<typeof listLeadsQuerySchema>;

export interface LeadSummary {
  id: string;
  firstName: string | null;
  lastName: string;
  fullName: string;
  businessName: string | null;
  title: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  source: string;
  verticals: string[];
  businessManager: BusinessManagerRef | null;
  convertedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LeadDetail extends LeadSummary {
  mobile: string | null;
  website: string | null;
  businessCategory: string | null;
  description: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  customFields: Record<string, unknown>;
  /** Populated once converted, so the record links to what it became. */
  convertedAccount: RecordRef | null;
  convertedContact: RecordRef | null;
  convertedDeal: RecordRef | null;
}

/**
 * Conversion input. Each of the three targets can either be created from the
 * lead's own fields or attached to an existing record, which is what makes the
 * wizard useful when the business is already an account.
 */
export const convertLeadSchema = z
  .object({
    /** Attach to this account instead of creating one. */
    accountId: uuidSchema.optional(),
    /** Overrides the name taken from the lead's businessName. */
    accountName: z.string().trim().min(1).max(200).optional(),
    /** Attach to this contact instead of creating one. */
    contactId: uuidSchema.optional(),
    createDeal: z.boolean().default(true),
    dealName: z.string().trim().min(1).max(200).optional(),
    dealAmount: optionalNumber(z.coerce.number().min(0).max(1e15)),
    dealCloseDate: optionalDate(),
    pipelineId: uuidSchema.optional(),
    stageId: uuidSchema.optional(),
    /** Business manager of everything created; defaults to the lead's business manager. */
    businessManagerId: uuidSchema.optional(),
  })
  .refine((value) => !value.createDeal || Boolean(value.dealName), {
    message: 'Name the opportunity, or turn off deal creation',
    path: ['dealName'],
  });
export type ConvertLeadInput = z.infer<typeof convertLeadSchema>;

export interface ConvertLeadResult {
  lead: LeadSummary;
  account: RecordRef;
  contact: RecordRef;
  deal: RecordRef | null;
}
